// Funciones puras sobre el grafo del editor (nodos/aristas de React Flow): geometria, anidado, plegado, vistas derivadas
// (resaltado, aristas agregadas), busqueda, ordenado automatico y alineacion. No tocan estado de React.
import dagre from "@dagrejs/dagre";
import { MarkerType } from "@xyflow/react";
import { infoServicio } from "modAws/CatalogoAws";

export const ANCHO_NODO = 120, ALTO_NODO = 96, CONT_W = 300, CONT_H = 200, ALTO_CABECERA = 36;

export const esContenedor = (n) => n.type === "grupo" || !!(n.data && n.data.contenedor);
export const anchoDe = (n) => Number(n.width ?? n.measured?.width ?? n.style?.width) || (esContenedor(n) ? CONT_W : ANCHO_NODO);
export const altoDe = (n) => Number(n.height ?? n.measured?.height ?? n.style?.height) || (esContenedor(n) ? CONT_H : ALTO_NODO);

export const mapa = (nodes) => Object.fromEntries(nodes.map(n => [n.id, n]));
// Posicion absoluta de un nodo (las posiciones de los hijos son relativas al padre).
export const absoluta = (n, porId) => {
    let x = n.position.x, y = n.position.y, p = n.parentId && porId[n.parentId];
    while (p) { x += p.position.x; y += p.position.y; p = p.parentId && porId[p.parentId]; }
    return { x, y };
};
export const ancestros = (n, porId) => { const a = []; for (let p = n.parentId && porId[n.parentId]; p; p = p.parentId && porId[p.parentId]) a.push(p); return a; };
export const profundidad = (n, porId) => ancestros(n, porId).length;
export const esDescendiente = (n, ancestroId, porId) => ancestros(n, porId).some(p => p.id === ancestroId);
export const descendientesDe = (id, nodes) => { const porId = mapa(nodes); return nodes.filter(n => esDescendiente(n, id, porId)); };

// El padre siempre antes que sus hijos (lo exige React Flow).
export const ordenar = (nodes) => {
    const porId = mapa(nodes), vistos = new Set(), salida = [];
    const visitar = (n) => { if (vistos.has(n.id)) return; vistos.add(n.id); if (n.parentId && porId[n.parentId]) visitar(porId[n.parentId]); salida.push(n); };
    nodes.forEach(visitar);
    return salida;
};
// Nodo mas interno que contiene el punto (cualquier nodo puede ser el destino); sin contar al propio nodo ni a sus descendientes.
export const destinoEn = (nodes, punto, excluirId) => {
    const porId = mapa(nodes);
    let mejor = null, mejorProf = -1;
    for (const n of nodes) {
        if (n.hidden || n.id === excluirId || (excluirId && esDescendiente(n, excluirId, porId))) continue;
        const a = absoluta(n, porId);
        if (punto.x < a.x || punto.x > a.x + anchoDe(n) || punto.y < a.y || punto.y > a.y + altoDe(n)) continue;
        const prof = profundidad(n, porId);
        if (prof >= mejorProf) { mejor = n; mejorProf = prof; }
    }
    return mejor;
};
// Un servicio pasa a ser contenedor (y crece) cuando se le suelta algo encima.
export const hacerContenedor = (nodes, id) => nodes.map(n => (n.id === id && !esContenedor(n)
    ? { ...n, data: { ...n.data, contenedor: true }, width: CONT_W, height: CONT_H } : n));

const limitar = (v, min, max) => Math.max(min, Math.min(v, Math.max(min, max)));
// Cambia el padre de un nodo conservando su posicion en pantalla (dentro de los limites del nuevo padre).
export const reubicar = (nodes, id, nuevoPadreId) => {
    const porId = mapa(nodes), n = porId[id];
    if (!n || (n.parentId || null) === (nuevoPadreId || null)) return nodes;
    const abs = absoluta(n, porId), padre = nuevoPadreId ? porId[nuevoPadreId] : null;
    const pa = padre ? absoluta(padre, porId) : { x: 0, y: 0 };
    let x = abs.x - pa.x, y = abs.y - pa.y;
    if (padre) { x = limitar(x, 8, anchoDe(padre) - anchoDe(n) - 8); y = limitar(y, 34, altoDe(padre) - altoDe(n) - 8); }
    const { parentId, extent, ...resto } = n;
    return ordenar(nodes.map(m => (m.id === id ? { ...resto, position: { x, y }, ...(padre ? { parentId: padre.id } : {}) } : m)));
};

// ---------- plegar / desplegar contenedores ----------
// Un contenedor plegado se reduce a su cabecera (data.colapsado, data.altoPrevio guarda el alto de antes) y oculta lo que contiene.
export const aplicarOcultos = (nodes) => {
    const porId = mapa(nodes);
    return nodes.map(n => {
        const oculto = ancestros(n, porId).some(p => p.data && p.data.colapsado);
        return !!n.hidden === oculto ? n : { ...n, hidden: oculto };
    });
};
const plegarNodo = (n) => ({ ...n, data: { ...n.data, colapsado: true, altoPrevio: altoDe(n) }, height: ALTO_CABECERA });
const desplegarNodo = (n) => {
    const { altoPrevio, ...datos } = n.data;
    return { ...n, data: { ...datos, colapsado: false }, height: altoPrevio || CONT_H };
};
export const alternarColapso = (nodes, id) => aplicarOcultos(nodes.map(n => {
    if (n.id !== id || !esContenedor(n)) return n;
    return n.data.colapsado ? desplegarNodo(n) : plegarNodo(n);
}));
// Pliega (o despliega) todos los contenedores de un tipo, p. ej. las capas.
export const plegarTodos = (nodes, plegar, filtro = (n) => n.data && n.data.tipo === "capa") => aplicarOcultos(nodes.map(n => {
    if (!esContenedor(n) || !filtro(n)) return n;
    if (plegar && !n.data.colapsado) return plegarNodo(n);
    if (!plegar && n.data.colapsado) return desplegarNodo(n);
    return n;
}));

// ---------- carga / guardado ----------
export const aEditor = (g) => {
    const nodes = (g.nodes || []).map(({ extent, ...n }) => {
        const base = { ...n, ...(n.style && n.style.width ? { width: n.style.width, height: n.style.height } : {}) };
        // plegado: en el archivo queda el alto desplegado (style.height); en pantalla solo la cabecera
        return n.data && n.data.colapsado ? { ...base, data: { ...n.data, altoPrevio: n.style && n.style.height ? n.style.height : n.data.altoPrevio }, height: ALTO_CABECERA } : base;
    });
    return { nodes: aplicarOcultos(ordenar(nodes)), edges: g.edges || [] };
};
// Para guardar: el tamano de los contenedores va en style (lo que valida el backend).
export const aGrafo = (nodes, edges) => ({
    nodes: nodes.map(n => ({
        id: n.id, type: n.type, position: n.position, data: n.data,
        ...(n.parentId ? { parentId: n.parentId } : {}),
        ...(esContenedor(n) ? { style: { width: anchoDe(n), height: n.data && n.data.colapsado ? (n.data.altoPrevio || CONT_H) : altoDe(n) } } : {}),
    })),
    edges: edges.filter(e => !(e.data && e.data.agregada)).map(e => ({ id: e.id, source: e.source, target: e.target, ...(e.label ? { label: e.label } : {}), ...(TIPOS_CONEXION[e.tipo] ? { tipo: e.tipo } : {}) })),
});

// ---------- vistas derivadas (no se guardan) ----------
const INFERIDAS = new Set(["por nombre", "estado remoto"]);
// Tipos de conexion que se pueden asignar (o que trae el importador): cada uno con su color/trazo.
export const TIPOS_CONEXION = {
    trafico: { nombre: "Tráfico", stroke: "#6f42c1" },
    datos: { nombre: "Datos / replicación", stroke: "#198754", strokeDasharray: "2 3" },
    dependencia: { nombre: "Dependencia", stroke: "#6c757d", strokeDasharray: "6 4" },
    evento: { nombre: "Evento / disparador", stroke: "#fd7e14", strokeDasharray: "1 4", strokeWidth: 2 },
};
const estiloBase = (e) => ({ ...(INFERIDAS.has(e.label) ? { strokeDasharray: "6 4" } : {}), ...(TIPOS_CONEXION[e.tipo] ? Object.fromEntries(Object.entries(TIPOS_CONEXION[e.tipo]).filter(([k]) => k !== "nombre")) : {}) });
const colorTipo = (e) => (TIPOS_CONEXION[e.tipo] ? TIPOS_CONEXION[e.tipo].stroke : undefined);
const flecha = (color) => ({ type: MarkerType.ArrowClosed, ...(color ? { color } : {}) });

// Lo que se dibuja: aristas que tocan nodos ocultos se reasignan al contenedor plegado (agrupadas, con contador);
// el nodo seleccionado y sus vecinos se resaltan y el resto se atenua.
export const construirVista = (nodes, edges, { selId = null, resaltar = true, marcas = {} } = {}) => {
    const porId = mapa(nodes);
    // contenedor plegado mas externo de cada nodo
    const visible = (id) => {
        const n = porId[id];
        if (!n) return id;
        const plegados = [n, ...ancestros(n, porId)].filter(x => x.data && x.data.colapsado);
        return plegados.length ? plegados[plegados.length - 1].id : id;
    };
    let aristas = edges;
    if (nodes.some(n => n.data && n.data.colapsado)) {
        const agr = new Map(), directas = [];
        for (const e of edges) {
            const s = visible(e.source), t = visible(e.target);
            if (s === t) continue;
            if (s === e.source && t === e.target) { directas.push(e); continue; }
            const k = `${s}>${t}`;
            if (!agr.has(k)) agr.set(k, { id: `agr-${s}-${t}`, source: s, target: t, n: 0, data: { agregada: true }, selectable: false, deletable: false });
            agr.get(k).n++;
        }
        aristas = [...directas, ...[...agr.values()].map(a => ({ ...a, label: a.n > 1 ? `${a.n}` : "" }))];
    }
    // nodos plegados: cuantos objetos esconden
    let vn = nodes.map(n => {
        if (!(n.data && n.data.colapsado)) return n;
        return { ...n, data: { ...n.data, nOculto: nodes.filter(m => esDescendiente(m, n.id, porId)).length } };
    });

    // marcas (p. ej. "ya no existe en AWS"): un contorno que se agrega a la clase del nodo, sin tocar sus datos
    if (Object.keys(marcas).length) vn = vn.map(n => (marcas[n.id] ? { ...n, className: [n.className, `deriva-${marcas[n.id]}`].filter(Boolean).join(" ") } : n));
    const sel = resaltar && selId && porId[selId] ? porId[selId] : null;
    if (!sel) return { nodes: vn, edges: aristas.map(e => ({ ...e, style: { ...estiloBase(e), ...(e.data && e.data.agregada ? { strokeWidth: 2 } : {}) }, ...(colorTipo(e) ? { markerEnd: flecha(colorTipo(e)) } : {}) })) };

    const nucleo = new Set([sel.id, ...descendientesDe(sel.id, nodes).map(n => n.id)]);
    const tocan = aristas.filter(e => nucleo.has(e.source) || nucleo.has(e.target));
    const vivos = new Set(nucleo);
    tocan.forEach(e => { vivos.add(e.source); vivos.add(e.target); });
    [...vivos].forEach(id => { const n = porId[id]; if (n) ancestros(n, porId).forEach(p => vivos.add(p.id)); });
    const idsTocan = new Set(tocan.map(e => e.id));
    vn = vn.map(n => (vivos.has(n.id) ? n : { ...n, className: "atenuado" }));
    return {
        nodes: vn,
        edges: aristas.map(e => (idsTocan.has(e.id)
            ? { ...e, style: { ...estiloBase(e), stroke: "#0d6efd", strokeWidth: 2.5 }, markerEnd: flecha("#0d6efd"), zIndex: 10 }
            : { ...e, style: { ...estiloBase(e), opacity: .12 }, ...(colorTipo(e) ? { markerEnd: flecha(colorTipo(e)) } : {}) })),
    };
};

// ---------- busqueda ----------
export const buscarNodos = (nodes, q) => {
    const t = q.trim().toLowerCase();
    if (t.length < 2) return [];
    const salida = [];
    for (const n of nodes) {
        const d = n.data || {}, info = infoServicio(d.servicio);
        const campos = [d.label, d.objeto, info && info.servicio.title, d.tf, d.resumen, ...(Array.isArray(d.ids) ? d.ids : [])];
        let puntos = 0, extra = "";
        campos.forEach((c, i) => { if (c && String(c).toLowerCase().includes(t)) puntos = Math.max(puntos, i === 0 ? 3 : 2); });
        if (!puntos && Array.isArray(d.props)) {
            const p = d.props.find(([k, v]) => String(v).toLowerCase().includes(t) || k.toLowerCase().includes(t));
            if (p) { puntos = 1; extra = `${p[0]}: ${p[1]}`; }
        }
        if (puntos) salida.push({ id: n.id, puntos, titulo: d.label || (info && info.servicio.title) || n.id, sub: d.objeto || d.tipo || "", extra, oculto: !!n.hidden });
    }
    return salida.sort((a, b) => b.puntos - a.puntos).slice(0, 12);
};

// ---------- alinear / distribuir (nodos seleccionados que comparten padre) ----------
export const alinear = (nodes, modo) => {
    const sel = nodes.filter(n => n.selected && !n.hidden);
    if (sel.length < 2) return nodes;
    const padre = sel[0].parentId || "";
    const rs = sel.filter(n => (n.parentId || "") === padre).map(n => ({ n, x: n.position.x, y: n.position.y, w: anchoDe(n), h: altoDe(n) }));
    if (rs.length < 2) return nodes;
    const minX = Math.min(...rs.map(r => r.x)), maxX = Math.max(...rs.map(r => r.x + r.w));
    const minY = Math.min(...rs.map(r => r.y)), maxY = Math.max(...rs.map(r => r.y + r.h));
    const nuevo = {};
    const fijar = (r, x, y) => { nuevo[r.n.id] = { x, y }; };
    if (modo === "izq") rs.forEach(r => fijar(r, minX, r.y));
    else if (modo === "der") rs.forEach(r => fijar(r, maxX - r.w, r.y));
    else if (modo === "centroH") rs.forEach(r => fijar(r, (minX + maxX) / 2 - r.w / 2, r.y));
    else if (modo === "arriba") rs.forEach(r => fijar(r, r.x, minY));
    else if (modo === "abajo") rs.forEach(r => fijar(r, r.x, maxY - r.h));
    else if (modo === "centroV") rs.forEach(r => fijar(r, r.x, (minY + maxY) / 2 - r.h / 2));
    else if (modo === "distH" || modo === "distV") {
        if (rs.length < 3) return nodes;
        const h = modo === "distH";
        const ord = [...rs].sort((a, b) => (h ? a.x - b.x : a.y - b.y));
        const libre = (h ? maxX - minX : maxY - minY) - ord.reduce((a, r) => a + (h ? r.w : r.h), 0);
        const hueco = libre / (ord.length - 1);
        let cursor = h ? minX : minY;
        ord.forEach(r => { fijar(r, h ? cursor : r.x, h ? r.y : cursor); cursor += (h ? r.w : r.h) + hueco; });
    }
    return nodes.map(n => (nuevo[n.id] ? { ...n, position: nuevo[n.id] } : n));
};

// ---------- ordenado automatico ----------
// Cada contenedor acomoda a sus hijos: los que estan conectados entre si en capas de izquierda a derecha (dagre) y los
// sueltos en una cuadricula debajo; el contenedor se ajusta a su contenido. Con `raizId` solo se reordena lo que contiene.
export const ordenarAuto = (nodes, edges, raizId = null) => {
    const porId = mapa(nodes), hijos = {}, res = {};
    nodes.forEach(n => { const k = n.parentId || ""; (hijos[k] = hijos[k] || []).push(n.id); });
    const reales = edges.filter(e => !(e.data && e.data.agregada));
    const nivelDe = (id, padreId) => { let n = porId[id]; while (n && (n.parentId || "") !== padreId) n = n.parentId ? porId[n.parentId] : null; return n ? n.id : null; };
    const PAD = 16, GAP = 24;

    const medir = (padreId) => {
        const hs = hijos[padreId] || [];
        const cabecera = padreId ? ALTO_CABECERA + 6 : 0, pad = padreId ? PAD : 0;
        hs.forEach(h => {
            const n = porId[h];
            if (esContenedor(n) && !(n.data && n.data.colapsado) && (hijos[h] || []).length) { const t = medir(h); res[h] = { w: t.w, h: t.h }; }
            else res[h] = { w: anchoDe(n), h: altoDe(n) };
        });
        const set = new Set(hs), pares = new Map();
        reales.forEach(e => {
            const a = nivelDe(e.source, padreId), b = nivelDe(e.target, padreId);
            if (a && b && a !== b && set.has(a) && set.has(b)) pares.set(`${a}>${b}`, [a, b]);
        });
        const conectados = new Set();
        pares.forEach(([a, b]) => { conectados.add(a); conectados.add(b); });
        let bw = 0, bh = 0;
        if (conectados.size) {
            const g = new dagre.graphlib.Graph();
            g.setGraph({ rankdir: "LR", nodesep: 30, ranksep: 90 });
            g.setDefaultEdgeLabel(() => ({}));
            conectados.forEach(id => g.setNode(id, { width: res[id].w, height: res[id].h }));
            pares.forEach(([a, b]) => g.setEdge(a, b));
            dagre.layout(g);
            conectados.forEach(id => { const p = g.node(id); res[id].x = pad + p.x - res[id].w / 2; res[id].y = cabecera + p.y - res[id].h / 2; });
            bw = g.graph().width || 0; bh = g.graph().height || 0;
        }
        const sueltos = hs.filter(h => !conectados.has(h));
        let sw = 0, sh = 0;
        if (sueltos.length) {
            const cols = Math.max(1, Math.ceil(Math.sqrt(sueltos.length)));
            const colW = Array(cols).fill(0), filaH = Array(Math.ceil(sueltos.length / cols)).fill(0);
            sueltos.forEach((id, i) => { colW[i % cols] = Math.max(colW[i % cols], res[id].w); filaH[Math.floor(i / cols)] = Math.max(filaH[Math.floor(i / cols)], res[id].h); });
            const y0 = cabecera + (bh ? bh + GAP : 0);
            sueltos.forEach((id, i) => {
                const c = i % cols, f = Math.floor(i / cols);
                res[id].x = pad + colW.slice(0, c).reduce((a, b) => a + b + GAP, 0);
                res[id].y = y0 + filaH.slice(0, f).reduce((a, b) => a + b + GAP, 0);
            });
            sw = colW.reduce((a, b) => a + b, 0) + (cols - 1) * GAP;
            sh = filaH.reduce((a, b) => a + b, 0) + (filaH.length - 1) * GAP;
        }
        return { w: Math.max(220, pad * 2 + Math.max(bw, sw)), h: Math.max(100, cabecera + (bh ? bh + (sh ? GAP : 0) : 0) + sh + pad) };
    };

    const total = medir(raizId || "");
    if (raizId) res[raizId] = { ...(res[raizId] || {}), w: total.w, h: total.h };
    return nodes.map(n => {
        const r = res[n.id];
        if (!r || (raizId && n.id === raizId && r.x === undefined)) {
            return raizId && n.id === raizId ? { ...n, width: r.w, height: r.h } : n;
        }
        const nuevo = { ...n, position: { x: r.x, y: r.y } };
        if (esContenedor(n) && (hijos[n.id] || []).length && !(n.data && n.data.colapsado)) { nuevo.width = r.w; nuevo.height = r.h; }
        return nuevo;
    });
};

// Agrega un fragmento de diagrama (nodos y aristas con ids propios) DEBAJO de lo que ya hay. Devuelve los nodos y aristas completos.
export const colocarFragmento = (nodes, edges, fragmento) => {
    const raices = nodes.filter(n => !n.parentId);
    const dx = raices.length ? Math.min(...raices.map(n => n.position.x)) : 0;
    const dy = raices.length ? Math.max(...raices.map(n => n.position.y + altoDe(n))) + 100 : 0;
    const nuevos = fragmento.nodes.map(n => (n.parentId ? n : { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } }));
    const ids = new Set(nodes.map(n => n.id));
    const sinChoque = nuevos.filter(n => !ids.has(n.id));
    const idsNuevos = new Set(sinChoque.map(n => n.id));
    return {
        nodes: aplicarOcultos(ordenar([...nodes, ...aEditor({ nodes: sinChoque, edges: [] }).nodes])),
        edges: [...edges, ...fragmento.edges.filter(e => idsNuevos.has(e.source) && idsNuevos.has(e.target))],
    };
};
